import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { usePriorityAccessModal } from '../../contexts/priority-access-modal-context';
import Analytics from '../../lib/analytics';

interface PriorityBannerProps {
  className?: string;
}

export const PriorityBanner: React.FC<PriorityBannerProps> = ({
  className,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { openModal } = usePriorityAccessModal();

  useEffect(() => {
    const handleScroll = () => {
      // Only hide banner on scroll for desktop screens
      if (window.innerWidth >= 600) {
        setIsScrolled(window.scrollY > 50);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Only hide banner on desktop, keep it visible on mobile
  if (isScrolled && window.innerWidth >= 600) return null;

  const handleClaimSpot = () => {
    // Track the click event
    Analytics.trackEvent('banner_cta_click', {
      event_category: 'Banner',
      event_label: 'Claim your spot today!',
      trigger_location: 'banner',
    });
    // Open the priority access modal
    openModal();
  };

  return (
    <div
      className={cn(
        'fixed top-0 right-0 left-0 z-[60]',
        'text-white shadow-lg',
        'transition-transform duration-300 ease-in-out',
        // Desktop behavior - hide/show on scroll
        isScrolled ? '-translate-y-full' : 'translate-y-0',
        // Mobile override - always visible, no transform
        'max-[479px]:!translate-y-0 max-[479px]:!transition-none',
        className
      )}
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-1.5 max-[479px]:py-3">
          {/* Banner Content */}
          <div className="flex flex-1 items-center justify-center text-center">
            <div className="font-primary flex items-center space-x-2 text-sm font-medium max-[479px]:space-x-1.5 max-[479px]:text-xs">
              <span className="text-lg max-[479px]:text-base">🚀</span>
              <span>Priority Access is open.</span>
              <button
                onClick={handleClaimSpot}
                className={cn(
                  'inline-flex items-center space-x-1 max-[479px]:space-x-0.5',
                  'hover:text-quaternary-100 text-white',
                  'underline decoration-2 underline-offset-2 max-[479px]:decoration-1',
                  'font-semibold transition-colors duration-200 max-[479px]:text-xs',
                  'group'
                )}
              >
                <span>Claim your spot today!</span>
                <ExternalLink className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5 max-[479px]:h-2.5 max-[479px]:w-2.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriorityBanner;
